import { TFunction } from 'i18next';
import { RentalBookingDetails } from '../types/bookingCreation';
import { InvoiceConfig, UseCase } from '../tenants/config';
import { formatCurrency } from './index';
import { PriceBreakdown, PriceLine } from '../api/booking/pricePreviewApiTypes';
import { roomNounOf } from './useCaseLabels';

// The invoice's line-item table (#1203 §2.2).
//
// Every number here comes from the backend `PriceBreakdown` — the same
// computation the details page renders — so the invoice and the screen cannot
// disagree, and the items sum to the invoiced total by construction. This file
// deliberately contains no price math: no weekend bucket, no per-night
// division of a stay total, no fallback that re-derives a price when the
// breakdown is missing. The caller blocks generation instead
// (`validateInvoiceData`), because a silent fallback is exactly the divergence
// this change removes.
//
// It lives apart from `invoicePdf.ts` so it can be exercised without pulling in
// jsPDF, which cannot be imported under jsdom.

export interface LineItem {
    description: string;
    quantity: string;
    unitPrice: string;
    amount: string;
}

export interface InvoiceLineItemsParams {
    booking: RentalBookingDetails;
    /** The one backend computation for this booking (#1203). */
    breakdown: PriceBreakdown;
    invoiceConfig: InvoiceConfig;
    /**
     * The NORMALIZED 2-letter code (`getCurrentLang()`), not the raw
     * `i18n.language` — that is 'de-DE' on a German browser, and service names
     * are keyed by 'de' / 'en'. Callers pass the same value the details page
     * looks names up by, so the invoice and the screen cannot name a service
     * differently.
     */
    language: 'de' | 'en';
    t: TFunction;
}

// Which invoice plural label a stay line is counted in. `base` (the collapsed
// booked total, emitted once the live rates stopped reproducing it) has no unit
// count by construction — it is one amount, not "n × a rate" — so it gets no
// quantity rather than being mislabelled. That is the defect this replaces: the
// old builder printed such a venue stay as "0 Nächte".
const STAY_UNIT_KEYS: Partial<Record<PriceLine['key'], string>> = {
    weekday: 'weekday',
    weekend: 'weekend',
    daily: 'night',
};

const pluralize = (
    t: TFunction,
    baseKey: string,
    count: number,
): string => {
    const suffix = count === 1 ? '_singular' : '_plural';
    return `${count} ${t(`invoice.${baseKey}${suffix}`)}`;
};

const buildObjectDescription = (params: InvoiceLineItemsParams): string => {
    const { booking, t } = params;
    // The room noun of the booking's use case (venue, apartment, hall); every
    // other value the apartment noun, as before.
    const serviceType = t(`invoice.service_type_${roomNounOf(booking.useCase)}`);
    const addr = booking.address;
    const addrLine = addr
        ? [
            addr.street,
            [addr.postalCode, addr.city].filter(Boolean).join(' '),
        ].filter(Boolean).join(', ')
        : '';
    return addrLine ? `${serviceType}\n${addrLine}` : serviceType;
};

/** The service name in the invoice's language, falling back to the backend's
 * own label and finally to a generic one. */
const serviceDescription = (
    line: PriceLine,
    params: InvoiceLineItemsParams,
): string => {
    const localized = line.langNames?.[params.language];
    return localized
        || line.label
        || params.t(
            line.compulsory
                ? 'invoice.cleaning_fee'
                : 'invoice.additional_service',
        );
};

export const buildLineItems = (
    params: InvoiceLineItemsParams,
): LineItem[] => {
    const { booking, breakdown, language, t } = params;
    const currency = params.invoiceConfig.currency ?? 'EUR';
    const money = (value: number) => formatCurrency(value, language, currency);

    const objectDescription = buildObjectDescription(params);

    // When bundling is enabled for this use case, compulsory fees and
    // additional services are folded into the main accommodation line
    // instead of getting their own rows (avoids a mixed-VAT optic). We
    // track the *last* main line's own amount so the extras are added only
    // to that line and the items still tie out to the gross total (even when
    // there are multiple main lines, e.g. a venue with weekday + weekend).
    const useCaseKey = booking.useCase?.toLowerCase() as UseCase | undefined;
    const bundleExtras = useCaseKey
        ? params.invoiceConfig.useCaseSettings?.[useCaseKey]?.bundleExtras
            === true
        : false;

    const items: LineItem[] = [];
    let lastMainAmount = 0;
    let hasMainItem = false;

    // Stay lines, in the order the backend emitted them.
    for (const line of breakdown.lines) {
        if (line.key === 'service') continue;
        const unitKey = STAY_UNIT_KEYS[line.key];
        const count = line.count;
        const hasUnits = count !== undefined && count > 0;
        lastMainAmount = line.amount;
        hasMainItem = true;
        let quantity = '';
        if (hasUnits) {
            quantity = unitKey
                ? pluralize(t, unitKey, count as number)
                : String(count);
        }
        items.push({
            description: objectDescription,
            quantity,
            // Derived from the line, not from a rate the invoice looked up:
            // amount ÷ units is what this booking was actually billed per unit,
            // including any volume discount the backend applied.
            unitPrice: hasUnits ? money(line.amount / (count as number)) : '',
            amount: money(line.amount),
        });
    }

    // Collect the extras (compulsory fees + additional services). They are
    // either emitted as their own rows or folded into the main line below.
    const extraNames: string[] = [];
    let extrasAmount = 0;
    const extraRows: LineItem[] = [];

    for (const line of breakdown.lines) {
        if (line.key !== 'service') continue;
        const name = serviceDescription(line, params);
        const quantity = line.quantity && line.quantity > 0 ? line.quantity : 1;
        extraNames.push(name);
        extrasAmount += line.amount;
        extraRows.push({
            description: name,
            quantity: String(quantity),
            unitPrice: money(line.amount / quantity),
            amount: money(line.amount),
        });
    }

    if (bundleExtras && hasMainItem && extraRows.length > 0) {
        // Fold the extras into the last main accommodation line: bump its
        // amount by the extras sum and append "inkl. {service names}" to
        // its description so the line items still sum to the gross total.
        const lastMainIndex = items.length - 1;
        const lastMain = items[lastMainIndex];
        const suffix = t('invoice.bundled_extras', {
            services: extraNames.join(', '),
        });
        items[lastMainIndex] = {
            ...lastMain,
            description: `${lastMain.description}\n${suffix}`,
            // Blank the unit price: once extras are folded in, unit price ×
            // quantity no longer equals the amount, which would read like an
            // arithmetic error. The quantity (e.g. "2 Nächte") still informs.
            unitPrice: '',
            amount: money(lastMainAmount + extrasAmount),
        };
    } else {
        items.push(...extraRows);
    }

    // The backend reports the adjustment signed: positive is a discount,
    // negative a surcharge (a manual final price above the computed one). Both
    // get a row — otherwise a surcharged booking's items would not add up to
    // the total printed below them.
    const { discount } = breakdown;
    if (discount !== 0) {
        items.push({
            description: t(
                discount > 0 ? 'invoice.discount' : 'invoice.surcharge',
            ),
            quantity: '',
            unitPrice: '',
            amount: `${discount > 0 ? '- ' : '+ '}${money(discount)}`,
        });
    }

    return items;
};
